<template lang="pug">
div#layer-manager
  .d-flex.align-center.mb-1
    .overline Layers
    v-btn.ml-1(icon small color="primary" title="Add Layer" @click="showNewLayerDialog = true")
      v-icon(small) library_add
    v-spacer
    v-checkbox(label="Highlight?" v-model="layerHighlighting" dense hide-details)

  .empty-state.pa-2(v-if="noLayers")
    .caption.grey--text.mb-2 No layers yet.
    v-btn.mb-3(small color="primary" @click="showNewLayerDialog = true")
      v-icon(left small) library_add
      | Add a Layer
    .caption.grey--text.mb-1 Or copy from another component:
    .d-flex.align-center
      v-select.mr-2(dense outlined label="Select a Template" v-model="componentIdToCopy" :items="copyableComponents" item-value="id" item-text="title" hide-details)
      v-btn(small color="primary" :disabled="!componentIdToCopy" @click="copyTemplate(componentIdToCopy)") Copy

  draggable(v-model="templateLayers" class="layer-list")
    v-list-item.layer-item(
      v-for="layer in templateLayers"
      :key="layer.id"
      @click="setActiveLayer(layer)"
      :class="{ 'layer-selected': isActive(layer) }"
    )
      v-list-item-avatar
        v-avatar(color="primary" size="30")
          span.white--text.body-2.text-capitalize {{ layer.type[0] }}
      v-list-item-content
        v-list-item-title {{ layer.name }}
      v-list-item-action(v-if="isActive(layer)")
        .d-flex
          v-btn(icon small @click.stop="copyLayer(layer)" title="Duplicate layer")
            v-icon(small) mdi-content-copy
          v-btn(icon small @click="toggleLayer(layer)" :title="layer.visible ? 'Hide layer' : 'Show layer'")
            v-icon(small) {{ layer.visible ? 'mdi-eye' : 'mdi-eye-off' }}
          v-btn(icon small @click="confirmDeleteLayer(layer)" title="Delete layer")
            v-icon(small color="red lighten-2") delete

  v-dialog(v-model="showNewLayerDialog" max-width="400")
    v-card
      v-card-title.subtitle-1 Add Layer
      v-card-text.pb-0 Choose a layer type:
      v-card-actions
        v-spacer
        v-btn(text small @click="showNewLayerDialog = false") Cancel
        v-btn(text small @click="createLayer('text')") Text
        v-btn(text small @click="createLayer('image')") Image
        v-btn(text small @click="createLayer('shape')") Shape

  v-dialog(v-model="showDeleteLayerDialog" max-width="360")
    v-card
      v-card-title.subtitle-1 Delete this layer?
      v-card-actions
        v-spacer
        v-btn(text small @click="showDeleteLayerDialog = false") Cancel
        v-btn(text small color="error" @click="deleteLayer(layerToDelete)") Delete
</template>

<script>
  import { filter } from 'lodash'
  import { mapGetters, mapActions } from 'vuex'
  import draggable from 'vuedraggable'

  export default {
    props: {
      template: {
        required: true
      }
    },

    components: { draggable },

    data() {
      return {
        showNewLayerDialog: false,
        showDeleteLayerDialog: false,
        editingVar: null,
        componentIdToCopy: null
      }
    },

    computed: {
      ...mapGetters([
        "findAllTemplateLayers",
        "findComponent",
        "findComponentTemplate",
        "activeLayer",
        "activeComponent",
        "allComponents",
      ]),

      layerHighlighting: {
        get() {
          return this.$store.getters["layerHighlighting"]
        },

        set(newSetting) {
          this.$store.commit("setLayerHighlighting", newSetting)
        }
      },

      editing: {
        get() {
          return this.editingVar
        },

        set(newEditing) {
          if(this.editingVar = newEditing) {
            this.$nextTick(() => {
              this.$refs.editingInput[0].select()
            })
          }
        }
      },

      templateLayers: {
        get() {
          return this.findAllTemplateLayers(this.template) || []
        },

        set(newLayers) {
          this.setLayersRenderOrder(newLayers)
        }
      },

      noLayers() {
        return this.templateLayers.length == 0
      },

      copyableComponents() {
        return filter(this.allComponents, (component) => {
          const notThisComponent = (component.templateId != this.template.id)

          return notThisComponent && this.hasLayers(component)
        })
      },
    },

    methods: {
      ...mapActions([
        "copyComponentTemplate",
        "createTemplateLayer",
        "copyTemplateLayer",
        "destroyTemplateLayer",
        "setLayersRenderOrder",
        "toggleLayer",
        "setActiveLayer",
      ]),

      hasLayers(component) {
        const
          template = this.findComponentTemplate(component),
          layers = template && template.layerIds,
          count = layers && layers.length || 0

        return count > 0
      },

      isActive(layer) {
        return this.activeLayer === layer
      },

      createLayer(layerType) {
        this.createTemplateLayer({ template: this.template, layerType })
        this.showNewLayerDialog = false
      },

      copyLayer(layer) {
        this.copyTemplateLayer({ template: this.template, layer })
      },

      copyTemplate() {
        const
          componentSource = this.findComponent(this.componentIdToCopy),
          templateToCopy = this.findComponentTemplate(componentSource)

        this.copyComponentTemplate({ component: this.activeComponent, template: templateToCopy })
      },

      confirmDeleteLayer(layer) {
        this.showDeleteLayerDialog = true
        this.layerToDelete = layer
      },

      deleteLayer() {
        this.destroyTemplateLayer({ template: this.template, layer: this.layerToDelete })
        this.showDeleteLayerDialog = false
      },
    }
  }
</script>

<style>
#layer-manager .layer-selected { background-color: rgba(0,0,0,0.06) !important; }
.theme--dark #layer-manager .layer-selected { background-color: rgba(255,255,255,0.1) !important; }
</style>

<style scoped>
.empty-state { border-top: 1px solid rgba(0,0,0,0.08); }
</style>
